import { getSrc } from 'gatsby-plugin-image';
import { APP_ROOT } from 'utils/constants/urls';
import { AllProductInfoType, FaqSchema } from 'utils/types';
import {
  discountedPrice,
  Promo,
  promoAppliesToProduct,
} from '../../utils/helpers/activePromoHelpers';
import {
  Availability,
  FaqJSONLDSchema,
  ItemListJSONLDSchema,
  ProductJSONLDSchema,
  SchemaType,
} from './types';

const config = require('../../utils/constants/siteConfig');

const schemaContext = 'https://schema.org';

const productURL = (slug: string): string => {
  return `${APP_ROOT}/products/${slug}`;
};

const listItem = (slug: string, index: number) => {
  return {
    '@type': SchemaType.LIST_ITEM,
    position: index + 1,
    url: productURL(slug),
  };
};

export const structuredProductList = (
  slugs: string[],
): ItemListJSONLDSchema => {
  return {
    '@context': schemaContext,
    '@type': SchemaType.ITEM_LIST,
    itemListElement: slugs.map((slug, i) => listItem(slug, i)),
  };
};

export const structuredProductData = (
  productData: AllProductInfoType,
  promo?: Promo,
  regionalRestrictionApplies?: boolean,
): ProductJSONLDSchema => {
  const {
    productId,
    name,
    description,
    slug,
    price: basePrice,
    outOfStock,
    heroSection,
    promotionable,
  } = productData;

  const url = productURL(slug);
  const image = getSrc(heroSection.heroSliderImages[0].gatsbyImageData);

  // Google crawlers expect "price" to match Google Merchant Feed "sale_price"
  // whenever one is set, otherwise it should be the base price
  const promoApplicable =
    promo && promoAppliesToProduct(productId, promo, promotionable);
  const price = discountedPrice(basePrice, promoApplicable ? promo : undefined);
  const formattedPrice = Number(price).toFixed(2);
  const availability =
    outOfStock || regionalRestrictionApplies
      ? Availability.OUT_OF_STOCK
      : Availability.IN_STOCK;
  const sku = productId.toString();

  const offers = {
    '@type': SchemaType.OFFER,
    sku,
    availability,
    price: formattedPrice,
    priceCurrency: 'USD',
  };

  // This schema must match the data provided to the Google Merchant Feed
  // These values are pulled from Contentful and Spree.
  return {
    '@context': schemaContext,
    '@type': SchemaType.PRODUCT,
    name,
    description,
    url,
    image,
    offers,
  };
};

// These values are defined at siteConfig.
export const structuredOrgData = {
  '@context': schemaContext,
  '@type': SchemaType.ORGANIZATION,
  name: config.author,
  legalName: config.legalName,
  url: config.authorUrl,
  logo: config.siteLogo,
  foundingDate: config.foundingDate,
  founders: {
    '@type': SchemaType.PERSON,
    name: config.founder,
  },
  address: {
    '@type': SchemaType.POSTAL_ADDRESS,
    streetAddress: config.streetAddress,
    addressLocality: config.addressLocality,
    addressRegion: config.addressRegion,
    postalCode: config.postalCode,
    addressCountry: config.addressCountry,
  },
  contactPoint: {
    '@type': SchemaType.CONTACT_POINT,
    contactType: config.contactType,
    telephone: config.telephone,
    email: config.email,
  },
  sameAs: [config.facebook, config.twitter, config.pinterest, config.linkedin],
};

// FAQ Schema can have many questions and answers. These values are pulled from Contentful.
export const structuredFaq = (faqs: FaqSchema): FaqJSONLDSchema => {
  return {
    '@context': schemaContext,
    '@type': SchemaType.FAQ_PAGE,
    mainEntity: faqs.map((faq) => ({
      '@type': SchemaType.QUESTION,
      name: faq.question,
      acceptedAnswer: {
        '@type': SchemaType.ANSWER,
        text: faq.answer,
      },
    })),
  };
};
